(() => {
  class ThemeCartDrawer {
    constructor(root) {
      this.root = root;
      this.isOpen = false;
      this.closeTimer = null;
      this.openAnimationFrame = null;
      this.closeTransitionPanel = null;
      this.closeTransitionHandler = null;
      this.isRefreshing = false;
      this.pendingRefresh = false;
      this.lastActiveElement = null;

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleDocumentChange = this.handleDocumentChange.bind(this);
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.handleDocumentFocusIn = this.handleDocumentFocusIn.bind(this);
      this.handleThemeCartUpdated = this.handleThemeCartUpdated.bind(this);

      this.bindEvents();
      this.syncHeaderCount(this.getItemCount());
    }

    setRoot(nextRoot) {
      this.clearCloseTransitionWatcher();
      this.root = nextRoot;
      this.root.hidden = !this.isOpen;
      this.root.classList.toggle('is-visible', this.isOpen);
      this.root.setAttribute('aria-hidden', this.isOpen ? 'false' : 'true');
      this.syncHeaderCount(this.getItemCount());
    }

    bindEvents() {
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('change', this.handleDocumentChange);
      document.addEventListener('keydown', this.handleDocumentKeydown);
      document.addEventListener('focusin', this.handleDocumentFocusIn);
      document.addEventListener('theme:cart-updated', this.handleThemeCartUpdated);
    }

    getRootPath() {
      if (window.Shopify && window.Shopify.routes && typeof window.Shopify.routes.root === 'string') {
        return window.Shopify.routes.root;
      }
      return '/';
    }

    getRouteUrl(path) {
      const rootPath = this.getRootPath();
      const normalizedRootPath = rootPath.endsWith('/') ? rootPath : `${rootPath}/`;
      return `${normalizedRootPath}${String(path || '').replace(/^\/+/, '')}`;
    }

    getItemCount() {
      const count = Number.parseInt(this.root?.dataset.itemCount || '0', 10);
      return Number.isFinite(count) ? count : 0;
    }

    syncHeaderCount(count) {
      const cartLink = document.querySelector('.header__cart-link');
      if (!cartLink) return;

      cartLink.querySelectorAll('[data-header-cart-count]').forEach((countTarget) => {
        countTarget.textContent = String(count);
      });

      const badge = cartLink.querySelector('[data-header-cart-badge]');
      if (badge) {
        badge.hidden = count <= 0;
      }

      cartLink.setAttribute('aria-label', `Cart (${count})`);
    }

    getPanel() {
      if (!this.root) return null;
      return this.root.querySelector('.cart-drawer__panel');
    }

    getFocusableElements() {
      const panel = this.getPanel();
      if (!panel) return [];

      const selector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', ');

      return Array.from(panel.querySelectorAll(selector)).filter((element) => {
        if (!(element instanceof HTMLElement)) return false;
        if (element.hidden) return false;
        if (element.getAttribute('aria-hidden') === 'true') return false;
        return element.offsetParent !== null || element === document.activeElement;
      });
    }

    focusPanel() {
      const panel = this.getPanel();
      if (!(panel instanceof HTMLElement)) return;
      panel.focus({ preventScroll: true });
    }

    restoreFocus() {
      if (this.lastActiveElement instanceof HTMLElement && this.lastActiveElement.isConnected) {
        this.lastActiveElement.focus({ preventScroll: true });
        this.lastActiveElement = null;
        return;
      }

      this.lastActiveElement = null;
      const cartLink = document.querySelector('.header__cart-link');
      if (cartLink instanceof HTMLElement) {
        cartLink.focus({ preventScroll: true });
      }
    }

    handleTabKeydown(event) {
      const panel = this.getPanel();
      if (!panel) return;

      const focusableElements = this.getFocusableElements();
      if (focusableElements.length === 0) {
        event.preventDefault();
        this.focusPanel();
        return;
      }

      const activeElement = document.activeElement;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeInsidePanel = activeElement instanceof Node && panel.contains(activeElement);

      if (!activeInsidePanel) {
        event.preventDefault();
        if (event.shiftKey) {
          lastElement.focus({ preventScroll: true });
        } else {
          firstElement.focus({ preventScroll: true });
        }
        return;
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus({ preventScroll: true });
        return;
      }

      if (event.shiftKey && (activeElement === firstElement || activeElement === panel)) {
        event.preventDefault();
        lastElement.focus({ preventScroll: true });
      }
    }

    parseTransitionTime(value) {
      const normalized = String(value || '').trim();
      if (!normalized) return 0;

      if (normalized.endsWith('ms')) {
        const milliseconds = Number.parseFloat(normalized.slice(0, -2));
        return Number.isFinite(milliseconds) ? milliseconds : 0;
      }

      if (normalized.endsWith('s')) {
        const seconds = Number.parseFloat(normalized.slice(0, -1));
        return Number.isFinite(seconds) ? seconds * 1000 : 0;
      }

      const numericValue = Number.parseFloat(normalized);
      return Number.isFinite(numericValue) ? numericValue : 0;
    }

    getCloseDurationMs() {
      const panel = this.getPanel();
      if (!panel || typeof window.getComputedStyle !== 'function') return 440;

      const styles = window.getComputedStyle(panel);
      const durationValues = styles.transitionDuration.split(',').map((value) => this.parseTransitionTime(value));
      const delayValues = styles.transitionDelay.split(',').map((value) => this.parseTransitionTime(value));

      if (durationValues.length === 0) return 440;

      const totalTransitionTimes = durationValues.map((duration, index) => {
        const delay = delayValues[index] ?? delayValues[delayValues.length - 1] ?? 0;
        return duration + delay;
      });

      const longest = Math.max(...totalTransitionTimes);
      return longest > 0 ? longest : 0;
    }

    clearCloseTransitionWatcher() {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;

      if (this.closeTransitionPanel && this.closeTransitionHandler) {
        this.closeTransitionPanel.removeEventListener('transitionend', this.closeTransitionHandler);
      }

      this.closeTransitionPanel = null;
      this.closeTransitionHandler = null;
    }

    scheduleHideAfterClose() {
      if (!this.root || this.isOpen) return;

      const panel = this.getPanel();
      if (!panel) {
        this.root.hidden = true;
        return;
      }

      const finalize = () => {
        if (!this.root || this.isOpen) return;
        this.clearCloseTransitionWatcher();
        this.root.hidden = true;
      };

      this.clearCloseTransitionWatcher();
      this.closeTransitionPanel = panel;
      this.closeTransitionHandler = (event) => {
        if (event.target !== panel || event.propertyName !== 'transform') return;
        finalize();
      };
      panel.addEventListener('transitionend', this.closeTransitionHandler);

      const closeDuration = this.getCloseDurationMs();
      if (closeDuration <= 0) {
        finalize();
        return;
      }

      this.closeTimer = window.setTimeout(finalize, closeDuration + 120);
    }

    show() {
      if (!this.root) return;
      if (!(this.lastActiveElement instanceof HTMLElement) || !this.lastActiveElement.isConnected) {
        const activeElement = document.activeElement;
        this.lastActiveElement = activeElement instanceof HTMLElement && !this.root.contains(activeElement) ? activeElement : null;
      }
      this.clearCloseTransitionWatcher();
      if (this.openAnimationFrame) {
        window.cancelAnimationFrame(this.openAnimationFrame);
        this.openAnimationFrame = null;
      }
      this.root.hidden = false;
      this.root.setAttribute('aria-hidden', 'false');
      this.root.classList.remove('is-visible');
      // Flush styles so reopening after a quick close still transitions cleanly.
      this.root.offsetWidth;
      // Use two RAFs to ensure browser paints initial position before transition target.
      this.openAnimationFrame = window.requestAnimationFrame(() => {
        this.openAnimationFrame = window.requestAnimationFrame(() => {
          this.openAnimationFrame = null;
          if (!this.root) return;
          this.root.classList.add('is-visible');
          this.focusPanel();
        });
      });
      this.isOpen = true;
      document.documentElement.classList.add('theme-cart-drawer-open');
      document.body.classList.add('theme-cart-drawer-open');
    }

    close() {
      if (!this.root || !this.isOpen) return;
      if (this.openAnimationFrame) {
        window.cancelAnimationFrame(this.openAnimationFrame);
        this.openAnimationFrame = null;
      }
      this.root.setAttribute('aria-hidden', 'true');
      this.root.classList.remove('is-visible');
      this.isOpen = false;
      document.documentElement.classList.remove('theme-cart-drawer-open');
      document.body.classList.remove('theme-cart-drawer-open');
      this.scheduleHideAfterClose();
      this.restoreFocus();
    }

    async open(options = {}) {
      const { refresh = true } = options;
      this.show();
      if (refresh) await this.refresh();
    }

    async handleThemeCartUpdated(event) {
      if (event?.detail?.source === 'cart-drawer') return;
      await this.open({ refresh: true });
    }

    handleDocumentKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Tab') {
        this.handleTabKeydown(event);
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    }

    handleDocumentFocusIn(event) {
      if (!this.isOpen || !this.root) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (this.root.contains(target)) return;

      this.focusPanel();
    }

    async handleDocumentClick(event) {
      if (!this.root) return;

      const cartLink = event.target.closest('.header__cart-link');
      if (cartLink) {
        if (event.defaultPrevented) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (typeof event.button === 'number' && event.button !== 0) return;
        event.preventDefault();
        await this.open({ refresh: true });
        return;
      }

      const closeTrigger = event.target.closest('[data-cart-drawer-close]');
      if (closeTrigger && this.root.contains(closeTrigger)) {
        event.preventDefault();
        this.close();
        return;
      }

      const removeTrigger = event.target.closest('[data-cart-remove]');
      if (removeTrigger && this.root.contains(removeTrigger)) {
        event.preventDefault();
        const line = Number.parseInt(removeTrigger.dataset.line || '', 10);
        if (Number.isFinite(line) && line > 0) {
          await this.updateLineQuantity(line, 0);
        }
        return;
      }

      const decreaseTrigger = event.target.closest('[data-cart-qty-decrease]');
      if (decreaseTrigger && this.root.contains(decreaseTrigger)) {
        event.preventDefault();
        const line = Number.parseInt(decreaseTrigger.dataset.line || '', 10);
        if (!Number.isFinite(line) || line < 1) return;
        const input = this.root.querySelector(`[data-cart-qty-input][data-line="${line}"]`);
        if (!(input instanceof HTMLInputElement)) return;
        const current = Number.parseInt(input.value || '1', 10);
        const next = Math.max(1, (Number.isFinite(current) ? current : 1) - 1);
        if (next === current) return;
        input.value = String(next);
        await this.updateLineQuantity(line, next);
        return;
      }

      const increaseTrigger = event.target.closest('[data-cart-qty-increase]');
      if (increaseTrigger && this.root.contains(increaseTrigger)) {
        event.preventDefault();
        const line = Number.parseInt(increaseTrigger.dataset.line || '', 10);
        if (!Number.isFinite(line) || line < 1) return;
        const input = this.root.querySelector(`[data-cart-qty-input][data-line="${line}"]`);
        if (!(input instanceof HTMLInputElement)) return;
        const current = Number.parseInt(input.value || '1', 10);
        const next = (Number.isFinite(current) ? current : 1) + 1;
        input.value = String(next);
        await this.updateLineQuantity(line, next);
      }
    }

    async handleDocumentChange(event) {
      if (!this.root) return;
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (!target.matches('[data-cart-qty-input]')) return;
      if (!this.root.contains(target)) return;

      const line = Number.parseInt(target.dataset.line || '', 10);
      if (!Number.isFinite(line) || line < 1) return;

      const nextQuantity = Number.parseInt(target.value || '1', 10);
      const normalizedQuantity = Number.isFinite(nextQuantity) && nextQuantity > 0 ? nextQuantity : 1;
      if (normalizedQuantity !== nextQuantity) {
        target.value = String(normalizedQuantity);
      }

      await this.updateLineQuantity(line, normalizedQuantity);
    }

    setLineLoading(line, isLoading) {
      if (!this.root) return;
      const lineItem = this.root.querySelector(`[data-cart-line-item][data-line="${line}"]`);
      if (!lineItem) return;

      lineItem.classList.toggle('is-loading', isLoading);
      lineItem.querySelectorAll('button, input').forEach((control) => {
        if (!(control instanceof HTMLButtonElement || control instanceof HTMLInputElement)) return;
        control.disabled = isLoading;
      });
    }

    async updateLineQuantity(line, quantity) {
      if (!this.root) return;
      this.setLineLoading(line, true);

      try {
        const response = await fetch(`${this.getRootPath()}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            line,
            quantity,
          }),
        });

        if (!response.ok) throw new Error('Cart update failed');
        const cart = await response.json();
        const count = Number.parseInt(cart.item_count || 0, 10);
        this.syncHeaderCount(Number.isFinite(count) ? count : 0);
        await this.refresh();
        document.dispatchEvent(new CustomEvent('theme:cart-updated', { detail: { source: 'cart-drawer' } }));
      } catch {
        window.location.href = this.getRouteUrl('cart');
      } finally {
        this.setLineLoading(line, false);
      }
    }

    async fetchSectionHtml() {
      const sectionUrl = new URL(this.getRootPath(), window.location.origin);
      sectionUrl.searchParams.set('section_id', 'cart-drawer');

      const response = await fetch(sectionUrl.toString(), {
        method: 'GET',
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error('Unable to load cart drawer section');
      return response.text();
    }

    async refresh() {
      if (!this.root) return;
      if (this.isRefreshing) {
        this.pendingRefresh = true;
        return;
      }

      this.isRefreshing = true;
      try {
        const currentBody = this.root.querySelector('[data-cart-drawer-body]');
        const currentScrollTop = currentBody ? currentBody.scrollTop : 0;

        const html = await this.fetchSectionHtml();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const nextRoot = parsed.querySelector('[data-cart-drawer-root]');
        if (!nextRoot) return;

        this.root.dataset.itemCount = nextRoot.dataset.itemCount || '0';
        this.root.innerHTML = nextRoot.innerHTML;
        if (this.isOpen) {
          const nextBody = this.root.querySelector('[data-cart-drawer-body]');
          if (nextBody) nextBody.scrollTop = currentScrollTop;
          this.focusPanel();
        }
        this.syncHeaderCount(this.getItemCount());
      } catch {
        // Do not interrupt browsing flow if drawer refresh fails.
      } finally {
        this.isRefreshing = false;
        if (this.pendingRefresh) {
          this.pendingRefresh = false;
          await this.refresh();
        }
      }
    }
  }

  const initializeCartDrawer = (scope = document) => {
    const root = scope.querySelector('[data-cart-drawer-root]') || document.querySelector('[data-cart-drawer-root]');
    if (!root) return;

    if (window.themeCartDrawer instanceof ThemeCartDrawer) {
      window.themeCartDrawer.setRoot(root);
      return;
    }

    window.themeCartDrawer = new ThemeCartDrawer(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeCartDrawer(document), { once: true });
  } else {
    initializeCartDrawer(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeCartDrawer(event.target);
  });
})();
